import { Box, Button, Paper, Typography, useTheme } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SignatureCanvas from 'react-signature-canvas';

interface SignaturePadProps {
  /** Callback chiamata quando la firma cambia */
  onChange?: (dataUrl: string | null) => void;
  /** Callback chiamata al salvataggio */
  onSave?: (dataUrl: string) => void;
  /** Larghezza del canvas (default: 100%) */
  width?: number | string;
  /** Altezza del canvas (default: 200px) */
  height?: number;
  /** Label da mostrare sopra il pad */
  label?: string;
  /** Disabilita il componente */
  disabled?: boolean;
  /** Testo placeholder quando vuoto */
  placeholder?: string;
  /** Testo pulsante cancella */
  clearButtonText?: string;
  /** Testo pulsante salva */
  saveButtonText?: string;
  /** Valore iniziale (data URL dell'immagine) */
  initialValue?: string | null;
  /** Altezza minima in px dei pulsanti (default: dimensione small di MUI) */
  actionButtonMinHeight?: number;
}

function SignaturePad({
  onChange,
  onSave,
  width = '100%',
  height = 200,
  label,
  disabled = false,
  placeholder,
  clearButtonText,
  saveButtonText,
  initialValue,
  actionButtonMinHeight,
}: SignaturePadProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  const resolvedPlaceholder = placeholder ?? t('signature.drawSignature');
  const resolvedClearButtonText = clearButtonText ?? t('common.clear');
  const resolvedSaveButtonText = saveButtonText ?? t('common.confirm');
  const signatureRef = useRef<SignatureCanvas>(null);
  const [isEmpty, setIsEmpty] = useState(!initialValue);

  const penColor = theme.palette.text.primary;

  // Keep the latest onChange in a ref so the initialValue effect below does not
  // depend on its identity. Otherwise an unstable `onChange` from the parent
  // (e.g. a handler closing over an unstable mutation object) would retrigger
  // the effect, which in turn calls onChange again - classic infinite loop.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!signatureRef.current) {
      return;
    }

    if (initialValue) {
      // Aggiungi un piccolo delay per assicurarsi che il canvas sia montato
      const timeoutId = setTimeout(() => {
        if (signatureRef.current) {
          try {
            signatureRef.current.fromDataURL(initialValue);
            setIsEmpty(false);
          } catch (error) {
            console.warn('Errore nel caricamento della firma iniziale:', error);
            setIsEmpty(true);
          }
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    } else if (initialValue === null) {
      signatureRef.current.clear();
      setIsEmpty(true);
      onChangeRef.current?.(null);
    }
  }, [initialValue]);

  const handleEnd = useCallback(() => {
    if (!signatureRef.current) {
      return;
    }

    const isCanvasEmpty = signatureRef.current.isEmpty();
    setIsEmpty(isCanvasEmpty);

    if (onChange) {
      onChange(isCanvasEmpty ? null : signatureRef.current.toDataURL('image/png'));
    }
  }, [onChange]);

  const handleClear = useCallback(() => {
    signatureRef.current?.clear();
    setIsEmpty(true);
    onChange?.(null);
  }, [onChange]);

  const handleSave = useCallback(() => {
    if (signatureRef.current && !signatureRef.current.isEmpty()) {
      const dataUrl = signatureRef.current.toDataURL('image/png');
      onSave?.(dataUrl);
    }
  }, [onSave]);

  // Previene scroll durante il disegno su mobile
  useEffect(() => {
    const canvas = signatureRef.current?.getCanvas();
    if (!canvas) {
      return;
    }

    const preventScroll = (e: TouchEvent) => {
      if (e.cancelable) {
        e.preventDefault();
      }
    };

    canvas.addEventListener('touchstart', preventScroll, { passive: false });
    canvas.addEventListener('touchmove', preventScroll, { passive: false });

    return () => {
      canvas.removeEventListener('touchstart', preventScroll);
      canvas.removeEventListener('touchmove', preventScroll);
    };
  }, []);

  return (
    <Box sx={{ width }}>
      {label && (
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          {label}
        </Typography>
      )}
      <Paper
        variant="outlined"
        sx={{
          position: 'relative',
          height,
          overflow: 'hidden',
          backgroundColor: disabled ? 'action.disabledBackground' : 'background.paper',
          borderStyle: 'dashed',
          borderColor: isEmpty ? 'divider' : 'primary.main',
          transition: 'border-color 0.2s',
          touchAction: 'none',
        }}
      >
        <SignatureCanvas
          ref={signatureRef}
          penColor={penColor}
          canvasProps={{
            style: {
              width: '100%',
              height: '100%',
              cursor: disabled ? 'not-allowed' : 'crosshair',
            },
          }}
          onEnd={handleEnd}
          backgroundColor="transparent"
        />

        {isEmpty && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.disabled',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'none',
              userSelect: 'none',
            }}
          >
            {resolvedPlaceholder}
          </Typography>
        )}
      </Paper>
      <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={handleClear}
          disabled={disabled || isEmpty}
          fullWidth
          sx={{ minHeight: actionButtonMinHeight }}
        >
          {resolvedClearButtonText}
        </Button>

        {onSave && (
          <Button
            variant="contained"
            size="small"
            onClick={handleSave}
            disabled={disabled || isEmpty}
            fullWidth
            sx={{ minHeight: actionButtonMinHeight }}
          >
            {resolvedSaveButtonText}
          </Button>
        )}
      </Box>
    </Box>
  );
}

export { SignaturePad };
